import { Component, OnInit, EventEmitter, Output, ViewChild, ElementRef } from '@angular/core';
import { Filter } from 'src/app/model/maker-checker/filter';
import { RequestDetail } from 'src/app/model/maker-checker/request-detail';
import { RequestType } from 'src/app/model/maker-checker/request-type';
import { MultiSelectorDetails } from 'src/app/model/multi-selector-details';
import { messages } from 'src/app/resources/messages';
import { BackofficeService } from 'src/app/services/backoffice.service';
import { LoggingService } from 'src/app/services/logging.service';
import { MakerCheckerFilterFormComponent } from '../maker-checker-filter-form/maker-checker-filter-form.component';
import { RequestInput } from 'src/app/model/maker-checker/request-input';
import { ActivatedRoute } from '@angular/router';
import { catchError } from 'rxjs';

@Component({
  selector: 'maker-checker-search-panel',
  templateUrl: './maker-checker-search-panel.component.html',
  styleUrls: ['./maker-checker-search-panel.component.scss']
})
export class MakerCheckerSearchPanelComponent implements OnInit {
  @Output() requestEmitter: EventEmitter<RequestDetail & RequestType> = new EventEmitter<RequestDetail & RequestType>();

  constructor(private loggingService: LoggingService, private activatedRoute: ActivatedRoute, private backofficeService: BackofficeService) { }

  ngOnInit(): void {
    this.optionalTag = decodeURIComponent(this.activatedRoute.snapshot.paramMap.get('tag') as string);
    if (this.optionalTag != "null") {
      this.selectedTag.push(this.optionalTag);
    }
    this.getRequestTypes();
  }

  @ViewChild(MakerCheckerFilterFormComponent) mcFilterReference!: MakerCheckerFilterFormComponent;
  @ViewChild('tagsReference') tagsReference!: ElementRef;
  @ViewChild('searchBoxReference') searchBoxReference!: ElementRef;

  public requests: RequestDetail[] = [];
  public requestsCopy: RequestDetail[] = [];
  public tags: MultiSelectorDetails[] = [];
  public requestTypes: RequestType[] = [];
  public msg = messages;
  public optionalTag!: string;
  public optionalSelectedTag: any = {};
  private tagValues: Set<string> = new Set();

  public filterData!: Filter;
  public searchTerm: string = '';
  public selectedTag: string[] = [];
  public selectedRequestType!: RequestType;
  public requestIdSelected: string = '';
  public preventInitialFetchOnLoad: boolean = true;
  public enableLoadingMessage: boolean = false;
  public setVariableHeight: boolean = false;

  public viewRequest(data: RequestDetail) {
    let value: RequestDetail & RequestType = { ...data, ...this.selectedRequestType }
    this.requestEmitter.emit(value);
    this.requestIdSelected = value.requestId;
    this.loggingService.logCustomEvent(`Maker Checker - Request selected for viewing: ${data.requestId}`);
  }

  public updateFilter(data: Filter) {
    this.filterData = data;
    this.loggingService.logCustomEvent(`Maker Checker - Filter selected: ${data}`);
    //To prevent duplicate fetch as while initial set of RequestType Radio fetch is called in
    if (this.preventInitialFetchOnLoad) {
      this.preventInitialFetchOnLoad = false
    }
    else {
      if (this.optionalTag == "null") {
        this.tagsReference.nativeElement.resetSelectMenu();
      }
      this.fetchData();
    }
  }

  public setSelectedTag($event: any) {
    this.resetOptionalTag();
    this.selectedTag = $event.detail?.selectedItemArray.map((x: MultiSelectorDetails) => x.id);
    this.filterRecords();
    this.loggingService.logCustomEvent(`Maker Checker - Tag selected: ${this.selectedTag[0]}`);
    this.tagsReference.nativeElement.closeSelectionPanel();
  }

  public searchTermCleared(event: any) {
    this.searchTerm = '';
    this.filterRecords();
  }

  public searchTermChanged(event: any) {
    this.searchTerm = event.detail.text;
    this.filterRecords();
  }

  public resetFilters(): void {
    this.tagValues.clear();
    this.requestIdSelected = '';
    this.resetOptionalTag();
    this.searchBoxReference.nativeElement.clearSearchBox();
    this.tagsReference.nativeElement.resetSelectMenu();
    this.mcFilterReference.clearFilters(true);
    this.setVariableHeight = false;
  }

  private resetOptionalTag() {
    this.optionalTag = "null";
    this.optionalSelectedTag = {};
  }

  private filterRecords() {

    if (this.searchTerm && this.selectedTag.length == 0) {
      this.requests = this.requestsCopy.filter(x => (x.displayValue.value.includes(this.searchTerm) ||
        x.requestCreationUTCTime.includes(this.searchTerm) ||
        x.requesterName.includes(this.searchTerm) ||
        x.requestId.includes(this.searchTerm)));
    }
    else if (this.searchTerm && this.selectedTag.length > 0) {
      this.requests = this.requestsCopy.filter(x => (x.displayValue.value.includes(this.searchTerm) ||
        x.requestCreationUTCTime.includes(this.searchTerm) ||
        x.requesterName.includes(this.searchTerm) ||
        x.requestId.includes(this.searchTerm)) && x.tags.find(y => y == this.selectedTag[0]));
    }
    else if (this.searchTerm == '' && this.selectedTag.length > 0) {
      this.requests = this.requestsCopy.filter(x => x.tags.find(y => y == this.selectedTag[0]));
    }
    else if (this.searchTerm == '' && this.selectedTag.length == 0) {
      this.requests = this.requestsCopy;
    }
    this.pushTags();
    if (this.optionalTag != "null") {
      let tagToPush = { id: this.optionalTag, description: [this.optionalTag] };
      let tagExists = this.tags.find(x => x.id == this.optionalTag);
      if (tagExists === undefined) {
        this.tags.push(tagToPush);
        this.optionalSelectedTag = tagToPush;
      }
    }
  }

  public setSelectedRequestType(event: any) {
    this.selectedRequestType = this.requestTypes.find(x => x.id == event.detail.value)!;
    this.fetchData();
  }

  public fetchData() {
    this.enableLoadingMessage = true;
    if (this.selectedRequestType !== undefined && this.filterData.status) {
      let requestObject: RequestInput = {
        operationalProcessId: this.selectedRequestType.id,
        status: this.filterData.status.id,
        fromDate: this.filterData.fromDate,
        toDate: this.filterData.toDate,
        approvedBy: this.filterData.actionTakenBy.id,
        requestedBy: this.filterData.requester.id
      }
      if (this.filterData.status.id == 1) {
        this.backofficeService.getOpenRequests(requestObject).pipe(
          catchError((error) => {
            this.enableLoadingMessage = false;
            this.processResponse([]);
            return error;
          })
        ).subscribe((response: any) => {
          this.processResponse(response.data);
        })
      }
      else {
        this.backofficeService.getRequestByFilter(requestObject).pipe(
          catchError((error) => {
            this.enableLoadingMessage = false;
            this.processResponse([]);
            return error;
          })
        ).subscribe((response: any) => {
          this.processResponse(response.data);        
        })
      }
    }
  }

  private processResponse(requests: RequestDetail[]): void {
    this.requests = requests;
    this.requestsCopy = requests;
    this.filterRecords();
    if (this.requests.length > 0) {
      let timer = setTimeout(() => {
        this.executeFirstRequestClick();
        let heightTimer = setTimeout(() => {
          this.getRequestListContainerHeight();
          clearTimeout(heightTimer);
        }, 100)
        this.enableLoadingMessage = false;
        clearTimeout(timer);
      }, 100)
    }
    else {
      let obj: any = {};
      this.requestEmitter.emit(obj);
      this.enableLoadingMessage = false;
    }
    
  }

  private executeFirstRequestClick() {
    let element: HTMLElement = document.getElementsByClassName('request')[0] as HTMLElement;
    element?.click();
  }

  private pushTags() {
    this.tags = [];
    if (this.optionalTag == "null") {
      this.selectedTag.forEach((x) => {
        this.tagValues.add(x);
      });
    }
    this.requests.forEach((x) => {
      x.tags.forEach(y => this.tagValues.add(y));
    })
    let items = Array.from(this.tagValues);
    items.forEach(y => {
      this.tags.push(<MultiSelectorDetails>{
        id: y,
        description: [y]
      })
    })
    this.tagValues.clear();
  }

  private getRequestTypes() {
    this.backofficeService.getRequestTypes().subscribe(response => {
      this.requestTypes = response.data;
      this.selectedRequestType = this.requestTypes[0];
    })
  }

  public getRequestListContainerHeight(){
    this.setVariableHeight = true
    let div = document.getElementById('requestDetailPanel');
    let divHeight = div?.clientHeight;
    return divHeight ? (divHeight - 200)+'px' : '';
  }
}
